import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useUser } from '../../context/UserContext';
import { useNotification } from '../../context/NotificationContext';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import UniversalProductForm from './UniversalProductForm';
import UniversalProductDetails from './UniversalProductDetails';
import UniversalProductBulkUpload from './UniversalProductBulkUpload';
import SitemapImport from './SitemapImport';
import './UniversalProductLibrary.css';

const API_BASE = process.env.REACT_APP_SERVER_DOMAIN;

const CATEGORIES = [
  'Dairy & Eggs',
  'Produce',
  'Meat & Seafood',
  'Bakery',
  'Beverages',
  'Pantry',
  'Snacks',
  'Frozen Foods',
  'Health & Beauty',
  'Household'
];

const COUNTRIES = [
  { code: 'CA', name: 'Canada' },
  { code: 'US', name: 'United States' }
];

// Sources are loaded dynamically from stats API

// SVG Icons
const GlobeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
  </svg>
);

const PackageIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="16.5" y1="9.4" x2="7.5" y2="4.21"/><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/>
  </svg>
);

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);

const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);

const UploadIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/><path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/>
  </svg>
);

const EditIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);

const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
  </svg>
);


const VerifiedIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="#3b82f6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 12l2 2 4-4"/>
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill="#3b82f6" stroke="#3b82f6"/>
    <path d="M9 12l2 2 4-4" fill="none" stroke="#fff"/>
  </svg>
);

const ChevronUpIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="18 15 12 9 6 15"/>
  </svg>
);

const ChevronDownIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9"/>
  </svg>
);

export default function UniversalProductLibrary() {
  const { getAccessTokenSilently } = useAuth0();
  const { isSuperAdmin, isLoading: isUserLoading } = useUser();
  const notification = useNotification();
  const navigate = useNavigate();

  // View state
  const [activeTab, setActiveTab] = useState('products');
  const [activeView, setActiveView] = useState('list'); // list, form, details, bulkUpload
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  // Data state
  const [products, setProducts] = useState([]);
  const [stats, setStats] = useState(null);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isStatsLoading, setIsStatsLoading] = useState(true);

  // Filters
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const searchDebounceRef = useRef(null);
  const [selectedCountry, setSelectedCountry] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedBrand, setSelectedBrand] = useState('');
  const [selectedSource, setSelectedSource] = useState('all');
  const [imageFilter, setImageFilter] = useState('all');
  const [verifiedFilter, setVerifiedFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [page, setPage] = useState(1);
  const [limit] = useState(20);

  // Debounce search input
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setSearchQuery(searchInput);
    }, 300);
    return () => clearTimeout(searchDebounceRef.current);
  }, [searchInput]);

  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);

  // Auth check
  useEffect(() => {
    if (!isUserLoading && !isSuperAdmin()) {
      notification.error('Only Super Admins can access this page');
      navigate('/dashboard');
    }
  }, [isSuperAdmin, isUserLoading, navigate, notification]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      setIsStatsLoading(true);
      const token = await getAccessTokenSilently();
      const response = await axios.get(`${API_BASE}/api/universal-products/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      // Stats API may not be deployed yet - fail silently
    } finally {
      setIsStatsLoading(false);
    }
  }, [getAccessTokenSilently]);

  // Fetch products
  const fetchProducts = useCallback(async () => {
    try {
      setIsLoading(true);
      const token = await getAccessTokenSilently();
      const params = { page, limit, sort: sortBy, order: sortOrder };
      if (searchQuery.trim()) params.search = searchQuery.trim();
      if (selectedCountry !== 'all') params.country = selectedCountry;
      if (selectedCategory !== 'all') params.category = selectedCategory;
      if (selectedBrand) params.brand = selectedBrand;
      if (selectedSource !== 'all') params.source = selectedSource;
      if (imageFilter !== 'all') params.hasImage = imageFilter;
      if (verifiedFilter !== 'all') params.verified = verifiedFilter;

      const response = await axios.get(`${API_BASE}/api/universal-products/admin/list`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setProducts(response.data.products || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      // Products API may not be deployed yet - fail silently
    } finally {
      setIsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAccessTokenSilently, page, limit, sortBy, sortOrder, selectedCountry, selectedCategory, selectedBrand, selectedSource, imageFilter, verifiedFilter, searchQuery]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Load products on mount since products tab is default
  useEffect(() => {
    fetchProducts();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch products when on products tab and filters change
  const [hasLoadedProducts, setHasLoadedProducts] = useState(true);

  useEffect(() => {
    if (activeTab === 'products' && hasLoadedProducts) {
      fetchProducts();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, sortBy, sortOrder, selectedCountry, selectedCategory, selectedBrand, selectedSource, imageFilter, verifiedFilter, searchQuery]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [selectedCountry, selectedCategory, selectedBrand, selectedSource, imageFilter, verifiedFilter, sortBy, sortOrder, searchQuery]);

  const totalPages = Math.ceil(total / limit);

  // Handlers
  const handleView = (product) => {
    setSelectedProduct(product);
    setActiveView('details');
  };

  const handleEdit = (product) => {
    setSelectedProduct(product);
    setIsEditing(true);
    setActiveView('form');
  };

  const handleAdd = () => {
    setSelectedProduct(null);
    setIsEditing(false);
    setActiveView('form');
  };

  const handleBulkUpload = () => {
    setActiveView('bulkUpload');
  };

  const handleSitemapImport = () => {
    setActiveView('sitemapImport');
  };

  const handleSitemapImportComplete = () => {
    setActiveView('list');
    fetchProducts();
    fetchStats();
  };

  const handleDeleteClick = (product) => {
    setProductToDelete(product);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!productToDelete) return;
    setDeleteDialogOpen(false);
    try {
      const token = await getAccessTokenSilently();
      await axios.delete(`${API_BASE}/api/universal-products/admin/${productToDelete._id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      notification.success('Product deleted successfully');
      if (activeView === 'details' || activeView === 'form') {
        setActiveView('list');
        setSelectedProduct(null);
        setIsEditing(false);
      }
      fetchProducts();
      fetchStats();
    } catch (error) {
      notification.error(error.response?.data?.message || 'Failed to delete product');
    }
    setProductToDelete(null);
  };

  const handleFormSave = async () => {
    setActiveView('list');
    setSelectedProduct(null);
    setIsEditing(false);
    fetchProducts();
    fetchStats();
  };

  const handleBulkUploadComplete = () => {
    setActiveView('list');
    fetchProducts();
    fetchStats();
  };

  const handleBack = () => {
    setActiveView('list');
    setSelectedProduct(null);
    setIsEditing(false);
  };

  const handleVerifyToggle = async (product) => {
    try {
      const token = await getAccessTokenSilently();
      const newVerified = !product.dataQuality?.isVerified;
      await axios.put(`${API_BASE}/api/universal-products/admin/${product._id}`, {
        dataQuality: { ...product.dataQuality, isVerified: newVerified }
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const updatedProduct = {
        ...product,
        dataQuality: { ...product.dataQuality, isVerified: newVerified }
      };
      // Update in the products list
      setProducts(prev => prev.map(p => p._id === product._id ? updatedProduct : p));
      // Update selected product if viewing/editing
      if (selectedProduct?._id === product._id) {
        setSelectedProduct(updatedProduct);
      }
      notification.success(newVerified ? 'Product verified' : 'Product unverified');
      fetchStats();
    } catch (error) {
      notification.error('Failed to update verification status');
    }
  };

  const selectedProductIndex = products.findIndex(p => p._id === selectedProduct?._id);
  const canGoPrev = selectedProductIndex > 0;
  const canGoNext = selectedProductIndex >= 0 && selectedProductIndex < products.length - 1;

  const handlePrevProduct = () => {
    if (canGoPrev) {
      setSelectedProduct(products[selectedProductIndex - 1]);
    }
  };

  const handleNextProduct = () => {
    if (canGoNext) {
      setSelectedProduct(products[selectedProductIndex + 1]);
    }
  };

  const getQualityColor = (score) => {
    if (score >= 80) return 'green';
    if (score >= 60) return 'yellow';
    return 'red';
  };

  // Sub-views
  if (activeView === 'form') {
    return (
      <>
        <ConfirmDialog
          open={deleteDialogOpen}
          title="Delete Product"
          description={`Are you sure you want to delete "${productToDelete?.name}"? This action cannot be undone.`}
          onConfirm={handleDeleteConfirm}
          onCancel={() => {
            setDeleteDialogOpen(false);
            setProductToDelete(null);
          }}
          confirmText="Delete"
        />
        <UniversalProductForm
          product={isEditing ? selectedProduct : null}
          onClose={handleBack}
          onSave={handleFormSave}
          onDelete={isEditing ? () => handleDeleteClick(selectedProduct) : undefined}
          onVerify={isEditing ? () => handleVerifyToggle(selectedProduct) : undefined}
          onPrev={isEditing && canGoPrev ? handlePrevProduct : null}
          onNext={isEditing && canGoNext ? handleNextProduct : null}
          categories={CATEGORIES}
          countries={COUNTRIES}
        />
      </>
    );
  }

  if (activeView === 'details' && selectedProduct) {
    return (
      <>
        <ConfirmDialog
          open={deleteDialogOpen}
          title="Delete Product"
          description={`Are you sure you want to delete "${productToDelete?.name}"? This action cannot be undone.`}
          onConfirm={handleDeleteConfirm}
          onCancel={() => {
            setDeleteDialogOpen(false);
            setProductToDelete(null);
          }}
          confirmText="Delete"
        />
        <UniversalProductDetails
          product={selectedProduct}
          onClose={handleBack}
          onEdit={() => {
            setIsEditing(true);
            setActiveView('form');
          }}
          onDelete={() => handleDeleteClick(selectedProduct)}
          onVerify={() => handleVerifyToggle(selectedProduct)}
          onPrev={canGoPrev ? handlePrevProduct : null}
          onNext={canGoNext ? handleNextProduct : null}
        />
      </>
    );
  }

  if (activeView === 'bulkUpload') {
    return (
      <UniversalProductBulkUpload
        onClose={handleBack}
        onUploadComplete={handleBulkUploadComplete}
        categories={CATEGORIES}
        countries={COUNTRIES}
      />
    );
  }

  if (activeView === 'sitemapImport') {
    return (
      <SitemapImport
        onClose={handleBack}
        onImportComplete={handleSitemapImportComplete}
        categories={CATEGORIES}
        countries={COUNTRIES}
      />
    );
  }

  return (
    <div className="settings-page">
      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Product"
        description={`Are you sure you want to delete "${productToDelete?.name}"? This action cannot be undone.`}
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setDeleteDialogOpen(false);
          setProductToDelete(null);
        }}
        confirmText="Delete"
      />

      {/* Header */}
      <div className="upl-header">
        <div className="upl-header-left">
          <div>
            <h1 className="upl-header-title">Product Library</h1>
            <p className="upl-header-subtitle">Manage the universal product catalog</p>
          </div>
        </div>
        <div className="upl-header-actions">
          <button className="upl-btn outline" onClick={handleSitemapImport}>
            <GlobeIcon />
            Import Sitemap
          </button>
          <button className="upl-btn outline" onClick={handleBulkUpload}>
            <UploadIcon />
            Bulk Upload
          </button>
          <button className="upl-btn primary" onClick={handleAdd}>
            <PlusIcon />
            Add Product
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="upl-tabs">
        <button
          className={`upl-tab ${activeTab === 'products' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('products');
            if (!hasLoadedProducts) {
              setHasLoadedProducts(true);
              fetchProducts();
            }
          }}
        >
          Products
        </button>
        <button
          className={`upl-tab ${activeTab === 'dashboard' ? 'active' : ''}`}
          onClick={() => setActiveTab('dashboard')}
        >
          Dashboard
        </button>
      </div>

      {/* Content */}
      <div className="upl-scroll-content">
        {activeTab === 'dashboard' && (
          <div>
            {isStatsLoading ? (
              <div className="upl-loading">Loading statistics...</div>
            ) : stats ? (
              <>
                {/* Stats Grid */}
                <div className="upl-stats-grid">
                  <div className="upl-stat-card">
                    <div className="upl-stat-value">{(stats.totalProducts || 0).toLocaleString()}</div>
                    <div className="upl-stat-label">Total Products</div>
                  </div>

                  <div className="upl-stat-card">
                    <div className="upl-stat-value">{stats.dataQuality?.averageScore || 0}%</div>
                    <div className="upl-stat-label">Avg Quality Score</div>
                  </div>

                  <div className="upl-stat-card">
                    <div className="upl-stat-value">{(stats.dataQuality?.withImage || 0).toLocaleString()}</div>
                    <div className="upl-stat-label">With Images</div>
                  </div>

                  <div className="upl-stat-card">
                    <div className="upl-stat-value">{(stats.dataQuality?.verified || 0).toLocaleString()}</div>
                    <div className="upl-stat-label">Verified</div>
                  </div>
                </div>

                {/* Charts Row */}
                <div className="upl-charts-row">
                  <div className="upl-chart-card">
                    <h3 className="upl-chart-title">Products by Country</h3>
                    {(stats.byCountry || []).map((item) => (
                      <div key={item.country} className="upl-bar-item">
                        <div className="upl-bar-label-row">
                          <span className="upl-bar-label">{item.country}</span>
                          <span className="upl-bar-count">{item.count.toLocaleString()}</span>
                        </div>
                        <div className="upl-bar-track">
                          <div
                            className="upl-bar-fill purple"
                            style={{ width: `${stats.totalProducts ? (item.count / stats.totalProducts) * 100 : 0}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="upl-chart-card">
                    <h3 className="upl-chart-title">Products by Source</h3>
                    {(stats.bySources || []).map((item) => (
                      <div key={item.source} className="upl-bar-item">
                        <div className="upl-bar-label-row">
                          <span className="upl-bar-label">{(item.source || 'unknown').replace(/_/g, ' ')}</span>
                          <span className="upl-bar-count">{item.count.toLocaleString()}</span>
                        </div>
                        <div className="upl-bar-track">
                          <div
                            className="upl-bar-fill blue"
                            style={{ width: `${stats.totalProducts ? (item.count / stats.totalProducts) * 100 : 0}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Top Categories */}
                <div className="upl-chart-card">
                  <h3 className="upl-chart-title">Top Categories</h3>
                  <div className="upl-categories-grid">
                    {(stats.topCategories || []).map((item) => (
                      <div key={item.category} className="upl-category-card">
                        <div className="upl-category-count">{item.count}</div>
                        <div className="upl-category-name">{item.category}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <div className="upl-loading">No statistics available</div>
            )}
          </div>
        )}

        {activeTab === 'products' && (
          <div>
            {/* Filters */}
            <div className="upl-filters">
              <div className="upl-filters-row">
                <div className="upl-filter-group" style={{ gridColumn: 'span 1' }}>
                  <label>Search</label>
                  <div className="upl-search-wrapper">
                    <SearchIcon />
                    <input
                      type="text"
                      className="upl-filter-input"
                      placeholder="Search by name, brand, UPC..."
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                    />
                  </div>
                </div>
                <div className="upl-filter-group">
                  <label>Country</label>
                  <select
                    className="upl-filter-input"
                    value={selectedCountry}
                    onChange={(e) => setSelectedCountry(e.target.value)}
                  >
                    <option value="all">All Countries</option>
                    {COUNTRIES.map(c => (
                      <option key={c.code} value={c.code}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div className="upl-filter-group">
                  <label>Category</label>
                  <select
                    className="upl-filter-input"
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                  >
                    <option value="all">All Categories</option>
                    {CATEGORIES.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div className="upl-filter-group">
                  <label>Source</label>
                  <select
                    className="upl-filter-input"
                    value={selectedSource}
                    onChange={(e) => setSelectedSource(e.target.value)}
                  >
                    <option value="all">All Sources</option>
                    {(stats?.bySources || []).map(item => (
                      <option key={item.source} value={item.source}>
                        {(item.source || 'unknown').replace(/_/g, ' ')} ({item.count})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="upl-filters-row-2">
                <div className="upl-filter-group">
                  <label>Brand</label>
                  <input
                    type="text"
                    className="upl-filter-input"
                    placeholder="Filter by brand..."
                    value={selectedBrand}
                    onChange={(e) => setSelectedBrand(e.target.value)}
                  />
                </div>
                <div className="upl-filter-group">
                  <label>Images</label>
                  <select
                    className="upl-filter-input"
                    value={imageFilter}
                    onChange={(e) => setImageFilter(e.target.value)}
                  >
                    <option value="all">All</option>
                    <option value="true">With Images</option>
                    <option value="false">Without Images</option>
                  </select>
                </div>
                <div className="upl-filter-group">
                  <label>Verified</label>
                  <select
                    className="upl-filter-input"
                    value={verifiedFilter}
                    onChange={(e) => setVerifiedFilter(e.target.value)}
                  >
                    <option value="all">All</option>
                    <option value="true">Verified</option>
                    <option value="false">Unverified</option>
                  </select>
                </div>
                <div className="upl-filter-group">
                  <label>Sort By</label>
                  <select
                    className="upl-filter-input"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                  >
                    <option value="created_at">Created Date</option>
                    <option value="updated_at">Updated Date</option>
                    <option value="name">Name</option>
                    <option value="brand">Brand</option>
                    <option value="category">Category</option>
                    <option value="dataQuality.score">Quality Score</option>
                    <option value="matchCount">Match Count</option>
                  </select>
                </div>
                <div className="upl-filter-group">
                  <label>Order</label>
                  <select
                    className="upl-filter-input"
                    value={sortOrder}
                    onChange={(e) => setSortOrder(e.target.value)}
                  >
                    <option value="desc">Descending</option>
                    <option value="asc">Ascending</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Results Count */}
            <div className="upl-results-count">
              Showing {products.length} of {total} products
            </div>

            {/* Products Table */}
            <div className="upl-table-wrapper">
              {isLoading ? (
                <div className="upl-loading">Loading products...</div>
              ) : (
                <>
                  <table className="upl-table">
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Category</th>
                        <th>Country</th>
                        <th>Barcode</th>
                        <th>Quality</th>
                        <th>Matches</th>
                        <th>Source</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {products.map((product) => (
                        <tr key={product._id} className="upl-row-clickable" onClick={() => handleView(product)}>
                          <td>
                            <div className="upl-product-cell">
                              <div className="upl-thumb-wrapper">
                                {product.primaryImageUrl ? (
                                  <img
                                    src={product.primaryImageUrl}
                                    alt={product.name}
                                    className="upl-product-thumb"
                                  />
                                ) : (
                                  <div className="upl-product-thumb-placeholder">
                                    <PackageIcon />
                                  </div>
                                )}
                                {product.dataQuality?.isVerified && (
                                  <span className="upl-verified-badge"><VerifiedIcon size={14} /></span>
                                )}
                              </div>
                              <div>
                                <div className="upl-product-name">{product.name}</div>
                                <div className="upl-product-brand">{product.brand || 'No brand'}</div>
                                {product.size && <div className="upl-product-size">{product.size}</div>}
                              </div>
                            </div>
                          </td>
                          <td>
                            <div>{product.category}</div>
                            {product.subcategory && (
                              <div style={{ fontSize: 12, color: '#6b7280' }}>{product.subcategory}</div>
                            )}
                          </td>
                          <td>
                            <span className="upl-badge gray">{product.country}</span>
                          </td>
                          <td style={{ color: '#6b7280' }}>
                            {product.upc || product.ean || '-'}
                          </td>
                          <td>
                            <div className="upl-quality">
                              <div className="upl-quality-bar">
                                <div
                                  className={`upl-quality-fill ${getQualityColor(product.dataQuality?.score || 0)}`}
                                  style={{ width: `${product.dataQuality?.score || 0}%` }}
                                />
                              </div>
                              <span className="upl-quality-score">{product.dataQuality?.score || 0}</span>
                            </div>
                          </td>
                          <td>{product.matchCount || 0}</td>
                          <td>
                            <span className="upl-badge blue" style={{ textTransform: 'capitalize' }}>
                              {(product.source?.name || 'unknown').replace(/_/g, ' ')}
                            </span>
                          </td>
                          <td>
                            <div className="upl-actions">
                              <button
                                className="upl-action-btn edit"
                                title="Edit"
                                onClick={(e) => { e.stopPropagation(); handleEdit(product); }}
                              >
                                <EditIcon />
                              </button>
                              <button
                                className="upl-action-btn delete"
                                title="Delete"
                                onClick={(e) => { e.stopPropagation(); handleDeleteClick(product); }}
                              >
                                <TrashIcon />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {products.length === 0 && (
                    <div className="upl-empty">
                      <PackageIcon />
                      <p>No products found</p>
                    </div>
                  )}

                  {/* Pagination */}
                  {total > limit && (
                    <div className="upl-pagination">
                      <div className="upl-pagination-info">
                        Page {page} of {totalPages} ({total} total)
                      </div>
                      <div className="upl-pagination-buttons">
                        <button
                          className="upl-pagination-btn"
                          disabled={page <= 1}
                          onClick={() => setPage(p => p - 1)}
                        >
                          Previous
                        </button>
                        <button
                          className="upl-pagination-btn"
                          disabled={page >= totalPages}
                          onClick={() => setPage(p => p + 1)}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
